import Maybe from 'graphql/tsutils/Maybe';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkflowUnstartedState } from './components/RsvpPageCard/utils';

import {
  useEventsQueryLazyQuery,
  usePagesQueryLazyQuery,
  usePrintPromotionMutation,
  useRegistrySummaryQueryLazyQuery,
  useSelectedThemeQueryLazyQuery,
  useWeddingWebsiteQueryLazyQuery,
} from './generated/graphql';
import { useVisiblePages } from './helpers/pages';
import questionsInfo from './helpers/questionsInfo';
import registryInfo from './helpers/registryInfo';
import useIsLiteSite from './helpers/useIsLiteSite';

export interface TrackingProps<T> {
  [K: string]: T;
}

interface Props {
  children: ReactNode | ReactNode[];
  /* only for testing */
  loaded?: boolean;
}

type MessagingVersion = 'hub' | 'single email' | 'no site hub';
type RsvpCardStates = 'workflow' | 'hidden' | 'active' | null;
type DateSelectionStates = 'exact' | 'season' | 'multi' | 'none';

export interface AnalyticsBaseProps {
  memberId?: string;
  customDomain: boolean;
  eventCount: number;
  hasWWSCoverPhoto: boolean;
  hasWWSWeddingDate: boolean;
  hasWWSWeddingLocation: boolean;
  isNewTemplate: boolean;
  platform: string;
  product: string;
  rsvpPreference: string;
  security: string;
  wwsPublishStatus: string;
  wwsTemplateVersion: string;
  wwsAdminVersion: string;
  wwsDateHidden: boolean;
  publicRSVPList?: boolean | null;
  wwsState?: string | null;
  wwsVanity?: string | null;
  wwsSlug?: string | null;
  pinProtect?: boolean | null;
  searchable?: boolean | null;
  currentTheme?: string | null;
  currentThemePartner?: string | null;
  familyTheme?: string | null;
  hasMatchingPaper: boolean;
  rsvpOnCount: number;
  hasWWSAccommodations: boolean;
  scheduleItemsCount: number;
  registryTypes: string[];
  hasRegistry: boolean;
  hasAnnouncement: boolean;
  announcementMessage: string | null;
  promo: string | null;
  messagingVersion: MessagingVersion;
  rsvpQuestionCount: number;
  rsvpQuestionCategories: string[];
  rsvpQuestionTypes: string[];
  experimentName?: string;
  experimentVariant?: string;
  rsvpCardState: RsvpCardStates;
  dateSelection: DateSelectionStates;
  unconfirmedSeason: string | null;
  wwsPages: Maybe<string>[];
  numPagesVisible: number;
  initializedWith: 'old template' | 'standard' | 'lite';
  url: string;
  source_location: string;
}

export type Track = (event: string, trackingData?: TrackingProps<any>) => void;

interface DefaultProps {
  fireOnce: (uniqueName: string, trackingCallback: () => void) => void;
  track: Track;
  baseProps?: AnalyticsBaseProps;
  loadingBaseProps: boolean;
  dashboardImpressionFired: boolean;
  setDashboardImpressionFired: (value: boolean) => void;
  /* Only should be called ones the cards are loaded */
  setPagesLoaded: (value: boolean) => void;
  setPageChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TrackingContext = createContext<DefaultProps>({
  fireOnce: noop,
  track: noop,
  loadingBaseProps: true,
  dashboardImpressionFired: false,
  setDashboardImpressionFired: noop,
  setPagesLoaded: noop,
  setPageChanged: noop,
});

export const TrackingProvider: React.FC<Props> = ({ children, loaded = false }) => {
  const [callWWQuery, { called, loading, data }] = useWeddingWebsiteQueryLazyQuery();
  const [
    callRegQuery,
    { called: calledRegistry, loading: loadingRegistry, data: registryData },
  ] = useRegistrySummaryQueryLazyQuery();
  const [
    callSelectedThemeQuery,
    { called: calledTheme, loading: loadingTheme, data: themeData },
  ] = useSelectedThemeQueryLazyQuery();
  const [callEventQuery, { called: calledEvents, loading: loadingEvents, data: eventData }] = useEventsQueryLazyQuery();
  const [callPagesQuery, { called: calledPages, loading: loadingPages, data: pagesData }] = usePagesQueryLazyQuery();
  const [
    callPromotion,
    { data: promotionData, loading: loadingPromotion, called: calledPromotion },
  ] = usePrintPromotionMutation();
  const [loadingBaseProps, setLoadingBaseProps] = useState(true);
  const [baseProps, setBaseProps] = useState<AnalyticsBaseProps | undefined>();
  const [trackCallQueue, setTrackCallQueue] = useState<TrackingProps<any>[]>([]);
  const [dashboardImpressionFired, setDashboardImpressionFired] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(loaded);
  const isliteSite = useIsLiteSite();
  const visiblePages = useVisiblePages();
  const [pageChanged, setPageChanged] = useState(false);

  const getMessagingVersion = useCallback((): MessagingVersion => {
    if (data?.weddingWebsite.state === 'uninitialized') {
      return 'no site hub';
    } else {
      return 'hub';
    }
  }, [data]);

  const { isUnstartedState } = useWorkflowUnstartedState();
  const rsvpCardState = useMemo((): RsvpCardStates => {
    if (isUnstartedState) return 'workflow';

    if (data?.guestWedding?.rsvpPageHidden === true) return 'hidden';
    else if (data?.guestWedding?.rsvpPageHidden === false) return 'active';

    return null;
  }, [data, isUnstartedState]);

  const getDateSelection = useCallback(() => {
    const trackValues: Pick<AnalyticsBaseProps, 'dateSelection' | 'unconfirmedSeason'> = {
      dateSelection: 'none',
      unconfirmedSeason: null,
    };
    if (data?.weddingWebsite.weddingDateFormat === 'range' && data?.weddingWebsite.weddingDateRangeStart) {
      trackValues.dateSelection = 'multi';
    } else if (data?.wedding?.weddingDate) {
      trackValues.dateSelection = 'exact';
    } else if (data?.wedding?.season) {
      trackValues.dateSelection = 'season';
      trackValues.unconfirmedSeason = `${data?.wedding?.season?.name} ${data?.wedding?.season?.year}`;
    }
    return trackValues;
  }, [data]);

  useEffect(() => {
    // Should be only called once when the pagesLoaded changes to true
    async function callAllQueries() {
      if (pagesLoaded === true) {
        await Promise.all([
          callWWQuery(),
          callRegQuery(),
          callSelectedThemeQuery(),
          callEventQuery(),
          callPagesQuery(),
          callPromotion(),
        ]);
      }
    }

    callAllQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesLoaded]);

  useEffect(() => {
    if (
      !loadingPages &&
      !loading &&
      !loadingTheme &&
      !loadingEvents &&
      !loadingRegistry &&
      !loadingPromotion &&
      called &&
      calledRegistry &&
      calledTheme &&
      calledEvents &&
      calledPages &&
      calledPromotion
    ) {
      const platform = /(iPhone|iPod|BlackBerry|Android)/.test(navigator.userAgent) ? 'mobile web' : 'desktop web';
      setLoadingBaseProps(false);

      const { hasAnyRegistry, registryTypes } = registryInfo(registryData?.registrySummary);
      const { rsvpQuestionCount, rsvpQuestionCategories, rsvpQuestionTypes } = questionsInfo(eventData, data);
      setBaseProps({
        source_location: window.location.href.includes('preview') ? 'EIP' : 'WWSM',
        url: window.location.pathname,
        memberId: data?.member?.id,
        customDomain: !!data?.weddingWebsite?.purchaseDomain,
        eventCount: eventData?.events?.length || 0,
        hasWWSCoverPhoto: !!data?.weddingWebsite?.coverPhoto,
        hasWWSWeddingDate: !!data?.wedding?.weddingDate,
        hasWWSWeddingLocation: !!data?.weddingWebsite?.weddingLocation,
        isNewTemplate: true,
        publicRSVPList: data?.guestWedding?.allowGuestPreview,
        platform,
        product: 'Guest Services',
        rsvpPreference: data?.guestWedding?.isPrivateRsvp ? 'secure' : 'public',
        security: data?.guestWedding?.isPrivateRsvp ? 'private' : 'public',
        wwsPublishStatus: data?.weddingWebsite?.state === 'published' ? 'published' : 'unpublished',
        wwsTemplateVersion: isliteSite ? 'Lite Site' : 'v2',
        wwsAdminVersion: '2.0',
        initializedWith: data?.weddingWebsite?.legacyMigrated ? 'old template' : isliteSite ? 'lite' : 'standard',
        wwsDateHidden: data?.weddingWebsite?.hideDate ?? false,
        wwsState: data?.weddingWebsite?.state,
        wwsVanity: data?.weddingWebsite?.vanityUrl,
        wwsSlug: data?.weddingWebsite?.slug,
        pinProtect: data?.weddingWebsite?.pinProtect,
        searchable: data?.weddingWebsite?.searchable,
        currentTheme: themeData?.theme?.name,
        familyTheme: themeData?.theme?.family || themeData?.theme?.name,
        currentThemePartner: themeData?.theme?.themeDesigner?.companyName,
        hasMatchingPaper: !!themeData?.theme?.designerUrl,
        rsvpOnCount: (eventData?.events || []).reduce((sum, event) => {
          if (event?.requireRsvp) {
            sum += 1;
          }
          return sum;
        }, 0),
        hasWWSAccommodations:
          flatten(
            (pagesData?.pages || []).map(page => (page?.items || []).filter(item => item.type === 'AccommodationItem'))
          ).length > 0,
        scheduleItemsCount: flatten((eventData?.events || []).map(event => event?.subEvents || [])).length,
        hasRegistry: hasAnyRegistry,
        registryTypes,
        hasAnnouncement: data?.weddingWebsite.announcement?.show ?? false,
        announcementMessage: data?.weddingWebsite.announcement?.message ?? null,
        promo: promotionData?.printPromotion?.title || null,
        messagingVersion: getMessagingVersion(),
        rsvpQuestionCategories,
        rsvpQuestionCount,
        rsvpQuestionTypes,
        rsvpCardState,
        numPagesVisible: visiblePages.length,
        wwsPages: visiblePages.map(page => page.title).filter(Boolean),
        ...getDateSelection(),
      });
    }
  }, [
    loading,
    loadingTheme,
    loadingEvents,
    loadingPages,
    loadingRegistry,
    loadingPromotion,
    data,
    eventData,
    themeData,
    pagesData,
    promotionData,
    registryData,
    setLoadingBaseProps,
    called,
    calledRegistry,
    calledTheme,
    calledEvents,
    calledPages,
    calledPromotion,
    getMessagingVersion,
    rsvpCardState,
    getDateSelection,
    isliteSite,
    visiblePages,
    pageChanged,
  ]);

  useEffect(() => {
    if (!isEmpty(baseProps) && !isEmpty(trackCallQueue)) {
      trackCallQueue.map(({ event, trackingData }) =>
        (window as any).analytics.track(event, { ...baseProps, ...(trackingData || {}) })
      );
      setTrackCallQueue([]);
    }
  }, [trackCallQueue, baseProps]);

  const track = (event: string, trackingData?: TrackingProps<any>) => {
    setTrackCallQueue([
      ...trackCallQueue,
      {
        event,
        trackingData,
      },
    ]);
  };

  const fireOnce = (uniqueName: string, trackingCallback: () => void) => {
    const hasBeenTracked = sessionStorage.getItem(uniqueName) === 'true';

    if (hasBeenTracked) return;

    try {
      trackingCallback();
    } catch (error) {
      console.warn('Tracking error.');
    } finally {
      sessionStorage.setItem(uniqueName, 'true');
    }
  };

  return (
    <TrackingContext.Provider
      value={{
        fireOnce,
        track,
        baseProps,
        loadingBaseProps,
        dashboardImpressionFired,
        setDashboardImpressionFired,
        setPagesLoaded,
        setPageChanged,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};
